import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminPageHeader, Btn } from '../admin/shell';
import { useAdminShell } from '../admin/shell/AdminShellContext';
import useContainerWidth from '../../hooks/useContainerWidth';
import { PILL, pillOf } from '../broadcast/studioUi';

/**
 * The pieces APP NOTICES' two tabs share (design_handoff_app_notices "Page layout",
 * "Lists", "Preview"): the hero with its status pill, the SHEETS · PUSH tab bar, the
 * layout maths, the list rows and the scaled preview card. Field kit = `broadcast/ui`
 * (SoftField / SoftText); section cards, checks and the CTA = `broadcast/studioUi`.
 */

/** Content width → the grid. ≥1080 is two columns with a sticky aside W = clamp(400, 34vw, 470). */
export function useNoticesLayout(ref) {
  const w = useContainerWidth(ref);
  const wide = w >= 1080;
  const narrow = w < 640;
  const previewCol = wide ? Math.min(470, Math.max(400, Math.round(w * 0.34))) : w;
  return { w, wide, narrow, previewCol, cols: wide ? `minmax(0,1fr) ${previewCol}px` : 'minmax(0,1fr)', twoCol: narrow ? '1fr' : '1fr 1fr' };
}

const STATUS_LOOK = {
  NEW: ['#2E2E2E', '#fff'],
  EDITING: ['#22ECFB', '#000'],
};
const statusLook = (status) => STATUS_LOOK[status] || ['#22C55E', '#000'];

/**
 * The black hero: eyebrow · APP NOTICES · status pill + subline · the action pills. `status`
 * is NEW / EDITING, or a flash word (PUBLISHED · SAVED · SENT · DRAFT SAVED) that lands green.
 */
export function NoticesHero({ status, subLine, actions }) {
  const [bg, fg] = statusLook(status);
  return (
    <AdminPageHeader
      section="notices"
      title="APP NOTICES"
      meta={(
        <span className="flex flex-wrap items-center gap-2 font-mono text-[10px] tracking-[0.14em] text-[#BDBDBD]">
          <span data-testid="notices-status" className="flex h-[22px] items-center rounded-full px-[10px] font-extrabold" style={{ background: bg, color: fg }}>{status}</span>
          <span>{subLine}</span>
        </span>
      )}
      actions={actions}
    />
  );
}

/** A hero action: `quiet` (#1F1F1F) or `white`. */
export function HeroButton({ variant = 'quiet', children, ...rest }) {
  return <Btn variant={variant} size="page" className="h-11 px-[18px] text-[11px] font-extrabold" {...rest}>{children}</Btn>;
}

const TABS = [
  { key: 'sheets', label: 'SHEETS', mark: 'SH', path: '/admin/notices', note: 'Interrupts once, then a 7-day quiet period. Shows as a bottom sheet on phones and a card on desktop.' },
  { key: 'push', label: 'PUSH', mark: 'PU', path: '/admin/notices/push', note: 'Lands on the lock screen and in notifications. Sent pushes are frozen; duplicate to reuse.' },
];

/** The white pill tab bar: 46px tabs with a 30px circle mark, the note beside them. */
export function NoticesTabs({ view }) {
  const navigate = useNavigate();
  const current = TABS.find((t) => t.key === view) || TABS[0];
  return (
    <nav className="flex flex-wrap items-center rounded-full bg-white" style={{ gap: '6px 14px', padding: 6 }} aria-label="App notice surfaces">
      <div className="flex" style={{ gap: 6 }}>
        {TABS.map((t) => {
          const on = t.key === view;
          return (
            <button key={t.key} type="button" aria-current={on ? 'page' : undefined} onClick={() => navigate(t.path)}
              className={`flex items-center whitespace-nowrap rounded-full font-mono text-[11px] font-extrabold tracking-[0.14em] transition-colors duration-200 ${on ? 'bg-[#0A0A0A] text-white' : 'bg-transparent text-black hover:bg-[#F2F2F2]'}`}
              style={{ height: 46, padding: '0 18px 0 8px', gap: 10 }}>
              <span className="flex flex-shrink-0 items-center justify-center rounded-full text-[10px] text-black" style={{ width: 30, height: 30, background: on ? '#fff' : '#F2F2F2' }}>{t.mark}</span>
              {t.label}
            </button>
          );
        })}
      </div>
      <span className="font-mono text-[10px] leading-[1.5] tracking-[0.08em] text-[#6B7280]" style={{ flex: '1 1 220px', padding: '0 12px' }}>{current.note}</span>
    </nav>
  );
}

/** The filter pills on a list card: a #F2F2F2 track of 32px pills. */
export function FilterPills({ options, value, onChange }) {
  return (
    <div className="flex" style={{ gap: 4, padding: 4, borderRadius: 999, background: '#F2F2F2' }} role="radiogroup">
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)} className={PILL}
          style={{ height: 32, padding: '0 14px', fontSize: 10, letterSpacing: '.12em', background: value === o.value ? '#0A0A0A' : 'transparent', color: value === o.value ? '#fff' : '#000' }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * One list row: 56×44 thumb · title + meta · stats · status pill · 34px action pills.
 * `editing` draws the #F2F2F2 row with the 2px inset ring.
 */
export function ListRow({ thumb, title, meta, stats = [], pill, actions = [], editing = false, testId }) {
  return (
    <div data-testid={testId} className="flex flex-wrap items-center" style={{ gap: 12, padding: '8px 10px 8px 8px', borderRadius: 22, background: editing ? '#F2F2F2' : '#F7F7F7', boxShadow: editing ? 'inset 0 0 0 2px #0A0A0A' : 'none' }}>
      <span className="flex flex-shrink-0 items-center justify-center overflow-hidden" style={{ width: 56, height: 44, borderRadius: 12, background: `${thumb.color || '#161616'} center/cover no-repeat`, backgroundImage: thumb.image ? `url(${thumb.image})` : 'none' }}>
        {!thumb.image && thumb.text && <span className="font-mono text-[7px] font-extrabold tracking-[0.08em]" style={{ color: thumb.ink || '#fff' }}>{thumb.text}</span>}
      </span>
      <div className="flex min-w-0 flex-col" style={{ flex: '1 1 220px', gap: 4 }}>
        <span className="truncate font-mono text-[12px] font-bold">{title}</span>
        <span className="truncate font-mono text-[9px] tracking-[0.12em] text-[#6B7280]">{meta}</span>
      </div>
      <div className="flex items-center font-mono text-[10px] tracking-[0.12em] text-[#6B7280]" style={{ gap: 14 }}>
        {stats.map((s) => (
          <span key={s.k} className="flex flex-col items-end" style={{ gap: 2 }}><b className="text-[12px] tracking-[0.02em] text-black">{s.v}</b>{s.k}</span>
        ))}
        {pill && <span className="flex items-center whitespace-nowrap rounded-full font-extrabold" style={{ height: 24, padding: '0 10px', fontSize: 9, letterSpacing: '.14em', background: pill.bg, color: pill.fg }}>{pill.label}</span>}
      </div>
      {actions.length > 0 && (
        <div className="flex" style={{ gap: 4 }}>
          {actions.map((a) => (
            <button key={a.label} type="button" onClick={a.onClick} disabled={a.disabled} className={`${PILL} hover:opacity-80`}
              style={{ height: 34, padding: '0 14px', fontSize: 10, letterSpacing: '.12em', ...(a.variant === 'solid' ? { background: '#0A0A0A', color: '#fff' } : a.variant === 'danger' ? { background: '#FEE2E2', color: '#B91C1C' } : { background: '#fff', color: '#000' }) }}>
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ListEmpty({ children }) {
  return <div className="text-center font-mono text-[11px] font-extrabold tracking-[0.14em] text-[#9CA3AF]" style={{ padding: '28px 12px' }}>{children}</div>;
}

/**
 * The preview aside: a white card with PREVIEW + a segmented toggle, the canvas scaled with
 * CSS `zoom` to fit the column. `canW` is the canvas width the zoom is computed from.
 */
export function PreviewCard({ title = 'PREVIEW', views, view, onView, previewCol, canW, children, sticky = true }) {
  const { isMobile } = useAdminShell();
  const zoom = Math.min(1, (previewCol - 28) / canW);
  return (
    <aside className="flex min-w-0 flex-col bg-white" style={{ position: sticky && !isMobile ? 'sticky' : 'static', top: 14, gap: 10, borderRadius: 28, padding: 14 }} data-testid="notice-preview">
      <div className="flex flex-wrap items-center justify-between" style={{ gap: 8, padding: '4px 6px 0' }}>
        <span className="font-mono text-[10px] font-extrabold tracking-[0.18em]">{title}</span>
        <div className="flex" style={{ gap: 4, padding: 4, borderRadius: 999, background: '#F2F2F2' }} role="radiogroup" aria-label="Preview view">
          {views.map((v) => (
            <button key={v.value} type="button" role="radio" aria-checked={view === v.value} onClick={() => onView(v.value)} className={PILL}
              style={{ height: 30, padding: '0 14px', fontSize: 10, letterSpacing: '.12em', background: view === v.value ? '#0A0A0A' : 'transparent', color: view === v.value ? '#fff' : '#000' }}>
              {v.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex justify-center">
        <div style={{ borderRadius: 20, overflow: 'hidden', zoom }}>{children}</div>
      </div>
    </aside>
  );
}

/** A small count helper for the label row: `12/60`, red at the cap. */
export const counterOf = (v, max) => ({ text: `${(v || '').length}/${max}`, color: (v || '').length >= max ? '#EF4444' : '#9CA3AF' });

/** The 56×34 ACTIVE / PAUSED switch inside a grey pill. */
export function ActiveSwitch({ on, onToggle }) {
  return (
    <button type="button" onClick={onToggle} role="switch" aria-checked={on} className={`${PILL} self-start`} style={{ height: 46, padding: '0 18px 0 6px', gap: 12, fontSize: 11, letterSpacing: '.14em', ...pillOf(false) }}>
      <span className="flex" style={{ width: 56, height: 34, padding: 3, boxSizing: 'border-box', justifyContent: on ? 'flex-end' : 'flex-start', borderRadius: 999, background: on ? '#22C55E' : '#CFCFCF', transition: 'background-color .2s' }}>
        <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#fff' }} />
      </span>
      {on ? 'ACTIVE' : 'PAUSED'}
    </button>
  );
}

/** Choice chip (accent, scope): black when on, #F2F2F2 when off. */
export function ChoiceChip({ on, onClick, children, style, ariaLabel, testId }) {
  return (
    <button type="button" aria-pressed={on} aria-label={ariaLabel} data-testid={testId} onClick={onClick} className={PILL} style={{ ...pillOf(on), ...style }}>{children}</button>
  );
}
